import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

// Declare posthog as any to avoid TypeScript issues
declare const posthog: any;

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private posthogInstance: any = null;
  private isPosthogEnabled: boolean = false;

  constructor() {
    this.isPosthogEnabled = environment.posthog?.enabled || false;
    if (this.isPosthogEnabled) {
      this.initializePostHog();
    }
  }

  private async initializePostHog() {
    try {
      const { default: posthog } = await import('posthog-js');
      this.posthogInstance = posthog;
    } catch (error) {
      console.warn('PostHog service initialization failed:', error);
    }
  }

  /**
   * Capture an event
   * @param eventName The name of the event
   * @param properties Additional properties to send with the event
   */
  capture(eventName: string, properties?: Record<string, any>): void {
    if (this.isPosthogEnabled && this.posthogInstance) {
      this.posthogInstance.capture(eventName, properties);
    } else if (!this.isPosthogEnabled) {
      console.log(`[PostHog Disabled] Would capture event: ${eventName}`, properties);
    }
  }

  /**
   * Identify a user
   * @param userId The user's unique identifier
   * @param properties User properties to set
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (this.isPosthogEnabled && this.posthogInstance) {
      this.posthogInstance.identify(userId, properties);
    } else if (!this.isPosthogEnabled) {
      console.log(`[PostHog Disabled] Would identify user: ${userId}`, properties);
    }
  }

  /**
   * Set user properties
   * @param properties Properties to set for the current user
   */
  setPersonProperties(properties: Record<string, any>): void {
    if (this.isPosthogEnabled && this.posthogInstance) {
      this.posthogInstance.setPersonProperties(properties);
    } else if (!this.isPosthogEnabled) {
      console.log(`[PostHog Disabled] Would set person properties:`, properties);
    }
  }

  /**
   * Reset the user session (useful for logout)
   */
  reset(): void {
    if (this.isPosthogEnabled && this.posthogInstance) {
      this.posthogInstance.reset();
    } else if (!this.isPosthogEnabled) {
      console.log(`[PostHog Disabled] Would reset user session`);
    }
  }

  /**
   * Set group properties
   * @param groupType The group type (e.g., 'company', 'organization')
   * @param groupKey The group identifier
   * @param properties Group properties to set
   */
  group(groupType: string, groupKey: string, properties?: Record<string, any>): void {
    if (this.isPosthogEnabled && this.posthogInstance) {
      this.posthogInstance.group(groupType, groupKey, properties);
    } else if (!this.isPosthogEnabled) {
      console.log(`[PostHog Disabled] Would set group: ${groupType}:${groupKey}`, properties);
    }
  }

  /**
   * Check if PostHog has been initialized
   */
  isInitialized(): boolean {
    return this.isPosthogEnabled && this.posthogInstance && this.posthogInstance.__loaded;
  }

  /**
   * Check if PostHog is enabled in the current environment
   */
  isEnabled(): boolean {
    return this.isPosthogEnabled;
  }
} 